import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (type: Toast['type'], title: string, message?: string, duration?: number) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);
console.log('Toast execute')

export function useToast() {
  const context = useContext(ToastContext);
  console.log('sdfdasdsdsda')
  
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, number>>(new Map());

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  const showToast = useCallback((
    type: Toast['type'],
    title: string,
    message?: string,
    duration: number = 5000
  ) => {
    const id = `toast_${Date.now()}_${Math.random()}`;
    const newToast: Toast = { id, type, title, message, duration };

    setToasts(prev => [...prev, newToast]);

    // Auto-dismiss
    const timer = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      timersRef.current.delete(id);
    }, duration);

    timersRef.current.set(id, timer);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} dismissToast={dismissToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, dismissToast }: { toasts: Toast[]; dismissToast: (id: string) => void }) {
  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-2 max-w-md pointer-events-none">
      {toasts.map((toast) => {
        const config = {
          success: {
            icon: CheckCircle2,
            bgColor: 'bg-gradient-to-r from-emerald-500 to-green-600',
          },
          error: {
            icon: XCircle,
            bgColor: 'bg-gradient-to-r from-red-500 to-rose-600',
          },
          warning: {
            icon: AlertTriangle,
            bgColor: 'bg-gradient-to-r from-amber-500 to-orange-600',
          },
          info: {
            icon: Info,
            bgColor: 'bg-gradient-to-r from-blue-500 to-#1e73be',
          },
        };

        const { icon: Icon, bgColor } = config[toast.type];

        return (
          <div
            key={toast.id}
            className={`${bgColor} text-white rounded-xl shadow-2xl p-4 pr-12 min-w-80 transform transition-all duration-300 ease-out pointer-events-auto relative`}
            style={{
              animation: 'slideInRight 0.3s ease-out',
            }}
          >
            <div className="flex items-start">
              <div className="text-white mr-3 mt-0.5">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm mb-0.5">{toast.title}</h4>
                {toast.message && (
                  <p className="text-xs text-white/90 leading-relaxed">{toast.message}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => dismissToast(toast.id)}
              className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
